import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';

const resolveDefaultApiBase = () => {
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:8003/api/v1';
  }
  const { protocol, hostname, port, host } = window.location;
  // In CRA dev server, call websocket service directly.
  if (String(port) === '3000') {
    return `${protocol}//${hostname}:8003/api/v1`;
  }
  // In nginx/prod, use reverse-proxied path.
  return `${protocol}//${host}/ws-api/v1`;
};

const readJsonResponse = async (response) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  const raw = await response.text();
  if (!contentType.includes('application/json')) {
    throw new Error(
      `Expected JSON from admin API but got ${contentType || 'unknown content type'}`
    );
  }
  try {
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    throw new Error('Admin API returned invalid JSON.');
  }
};

const RealtimeConnections = () => {
  const { getAuthHeaders } = useAuth();
  const apiBase = useMemo(
    () => process.env.REACT_APP_WS_ADMIN_API_BASE_URL || resolveDefaultApiBase(),
    []
  );

  const [jwtToken, setJwtToken] = useState(localStorage.getItem(scopedKey('ws_admin_jwt', getActiveAccountId())) || '');
  const [accountId, setAccountId] = useState('');
  const [userId, setUserId] = useState('');
  const [connections, setConnections] = useState([]);
  const [selectedBySocketId, setSelectedBySocketId] = useState({});
  const [eventName, setEventName] = useState('server:message');
  const [payloadText, setPayloadText] = useState('{"message":"Hello from admin"}');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const selectedSocketIds = useMemo(
    () => Object.entries(selectedBySocketId).filter(([, checked]) => checked).map(([socketId]) => socketId),
    [selectedBySocketId]
  );

  const saveToken = useCallback((nextToken) => {
    localStorage.setItem(scopedKey('ws_admin_jwt', getActiveAccountId()), nextToken);
  }, []);

  const refreshConnections = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    const hasAuth = jwtToken.trim() || (authHeaders && Object.keys(authHeaders).length > 0);
    if (!hasAuth) {
      setStatus('Provide admin JWT token or login with API key first.');
      return;
    }
    setLoading(true);
    setStatus('Loading active websocket connections...');
    try {
      const params = new URLSearchParams();
      if (accountId.trim()) {
        params.set('accountId', accountId.trim());
      }
      if (userId.trim()) {
        params.set('userId', userId.trim());
      }
      const authHeaders = getAuthHeaders();
      const headers = { ...authHeaders, 'Content-Type': 'application/json' };
      if (jwtToken.trim()) headers.Authorization = `Bearer ${jwtToken.trim()}`;
      const response = await fetch(`${apiBase}/ws-admin/connections?${params.toString()}`, {
        headers,
      });
      const data = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(data?.detail || `Request failed (${response.status})`);
      }
      setConnections(Array.isArray(data?.items) ? data.items : []);
      setSelectedBySocketId({});
      setStatus(`Loaded ${Number(data?.count || 0)} active connections.`);
    } catch (error) {
      setStatus(`Failed to load connections: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, getAuthHeaders, jwtToken, userId]);

  const pushToSelected = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    const hasAuth = jwtToken.trim() || (authHeaders && Object.keys(authHeaders).length > 0);
    if (!hasAuth) {
      setStatus('Provide admin JWT token or login with API key first.');
      return;
    }
    if (!selectedSocketIds.length) {
      setStatus('Select at least one connection.');
      return;
    }
    let payload = {};
    try {
      payload = payloadText.trim() ? JSON.parse(payloadText) : {};
    } catch (error) {
      setStatus('Payload must be valid JSON.');
      return;
    }
    setLoading(true);
    setStatus('Pushing message to selected clients...');
    try {
      const authHeaders = getAuthHeaders();
      const headers = { ...authHeaders, 'Content-Type': 'application/json' };
      if (jwtToken.trim()) headers.Authorization = `Bearer ${jwtToken.trim()}`;
      const response = await fetch(`${apiBase}/ws-admin/push`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          socketIds: selectedSocketIds,
          event: eventName.trim() || 'server:message',
          payload,
        }),
      });
      const data = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(data?.detail || `Request failed (${response.status})`);
      }
      const pushed = Number(data?.pushed || 0);
      const skipped = Array.isArray(data?.skippedSocketIds) ? data.skippedSocketIds.length : 0;
      setStatus(
        skipped
          ? `Message pushed to ${pushed} client(s). Skipped ${skipped} socket(s).`
          : `Message pushed to ${pushed} client(s).`
      );
    } catch (error) {
      setStatus(`Push failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiBase, eventName, getAuthHeaders, jwtToken, payloadText, selectedSocketIds]);

  return (
    <div className="container-fluid px-2 px-md-3 px-lg-4 py-3">
      <div className="row g-3">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h4 className="mb-1">Realtime Client Connections</h4>
              <p className="text-muted mb-3">
                Manage active Socket.IO client sessions and push messages to selected connections.
              </p>
              <div className="row g-2">
                <div className="col-12 col-md-6 col-xl-4">
                  <label className="form-label">Admin JWT</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Paste admin bearer token"
                    value={jwtToken}
                    onChange={(event) => {
                      const value = event.target.value;
                      setJwtToken(value);
                      saveToken(value);
                    }}
                  />
                </div>
                <div className="col-6 col-md-3 col-xl-2">
                  <label className="form-label">Account ID</label>
                  <input
                    className="form-control"
                    placeholder="Optional"
                    value={accountId}
                    onChange={(event) => setAccountId(event.target.value)}
                  />
                </div>
                <div className="col-6 col-md-3 col-xl-2">
                  <label className="form-label">User ID</label>
                  <input
                    className="form-control"
                    placeholder="Optional"
                    value={userId}
                    onChange={(event) => setUserId(event.target.value)}
                  />
                </div>
                <div className="col-12 col-xl-4 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    disabled={loading}
                    onClick={refreshConnections}
                  >
                    Refresh Connections
                  </button>
                  <span className="text-muted small">{status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h5 className="mb-2 mb-md-0">Active Connections ({connections.length})</h5>
                <div className="small text-muted">Selected: {selectedSocketIds.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '48px' }}>#</th>
                      <th>Client</th>
                      <th>Path</th>
                      <th>Socket ID</th>
                      <th>Account</th>
                      <th>User</th>
                      <th>Instance</th>
                      <th>Connected At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connections.map((item) => (
                      <tr key={item.socketId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={Boolean(selectedBySocketId[item.socketId])}
                            onChange={(event) =>
                              setSelectedBySocketId((prev) => ({
                                ...prev,
                                [item.socketId]: event.target.checked,
                              }))
                            }
                          />
                        </td>
                        <td>{item.clientName || '-'}</td>
                        <td className="text-break">{item.pagePath || '-'}</td>
                        <td className="text-break">{item.socketId}</td>
                        <td>{item.accountId ?? '-'}</td>
                        <td>{item.userId ?? '-'}</td>
                        <td className="text-break">{item.instanceId || '-'}</td>
                        <td>{item.connectedAt || '-'}</td>
                      </tr>
                    ))}
                    {!connections.length && (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4">
                          No active websocket connections found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">Push Message To Selected Connections</h5>
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <label className="form-label">Event Name</label>
                  <input
                    className="form-control"
                    value={eventName}
                    onChange={(event) => setEventName(event.target.value)}
                    placeholder="server:message"
                  />
                </div>
                <div className="col-12 col-md-8">
                  <label className="form-label">JSON Payload</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={payloadText}
                    onChange={(event) => setPayloadText(event.target.value)}
                  />
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={loading || !selectedSocketIds.length}
                  onClick={pushToSelected}
                >
                  Push To Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtimeConnections;
